import React, {useEffect, useMemo, useState} from 'react';
import {message} from "antd";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import Button from "antd/es/button/button";
import {PlusOutlined} from "@ant-design/icons";
import {PlaneList} from "./components/PlaneList";
import {PlaneCreateDrawer} from "./components/PlaneCreateDrawer";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {clearPlaneList, getPlaneList} from "../../../../store/reducers/fleetReducer/fleetReducer";
import {getTariff, clearTariff} from "../../../../store/reducers/tariffReducer/tariffReducer";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import Search from "antd/es/input/Search";
import {Col, Row} from "antd";
import {Loader} from "../../../../components/UI/Loader/Loader";
import {CustomResult} from "../../../../components/UI/AntCustom/CustomResult";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {useIsMobile} from "../../../../hooks/useWindowSize";
import {CustomFieldInput} from "../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomSearch} from "../../../../components/UI/AntCustom/CustomSearch";


const FleetPageMessages = defineMessages({
  title: {
    id: "fleetPage.title",
    defaultMessage: "Aircraft fleet",
  },
  search: {
    id: "fleetPage.search",
    defaultMessage: "Search by an aircraft registration number",
  },
  addPlane: {
    id: "fleetPage.addPlane",
    defaultMessage: "Add aircraft",
  },
  noPlanes: {
    id: "fleetPage.noPlanes",
    defaultMessage: "No aircraft",
  },
  trialPlaneLimitReached: {
    id: "planeCreateForm.trialPlaneLimitReached",
    defaultMessage: "Trial mode allows adding up to 7 aircraft. Please upgrade your subscription to add more.",
  },
  aircraftSlotLimitReached: {
    id: "planeCreateForm.aircraftSlotLimitReached",
    defaultMessage: "You have reached your aircraft slot limit. Ask your company administrator to buy more slots in Subscription.",
  },
})

const TRIAL_PLANE_LIMIT = 7;

export const FleetPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {items, isLoading, totalCount} = useAppSelector(state => state.fleet.itemsObject);
  const {totalCount: planeTotalCount} = useAppSelector(state => state.fleet.itemsObject);
  const tariff = useAppSelector(state => state.tariff.oneItemObject.item);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const isTrialLimitReached = useMemo(() => {
    const activeTrialPackage = tariff?.companyTariffPackages?.find(pkg => pkg.active && pkg.isTrial);
    if (!activeTrialPackage) return false;
    return planeTotalCount >= TRIAL_PLANE_LIMIT;
  }, [tariff, planeTotalCount]);

  const isAircraftSlotLimitReached = useMemo(() => {
    const activePaidPackage = tariff?.companyTariffPackages?.find(
      pkg => pkg.active && !pkg.isTrial && (pkg.subscribedAircraftCount ?? 0) > 0
    );
    if (!activePaidPackage) return false;
    const subscribed = activePaidPackage.subscribedAircraftCount ?? 0;
    const billed = activePaidPackage.billedAircraftCount ?? 0;
    return billed >= subscribed;
  }, [tariff]);

  const handleAddAircraftClick = () => {
    if (isTrialLimitReached) {
      message.warning(intl.formatMessage(FleetPageMessages.trialPlaneLimitReached));
      return;
    }
    if (isAircraftSlotLimitReached) {
      message.warning(intl.formatMessage(FleetPageMessages.aircraftSlotLimitReached));
      return;
    }
    setIsCreateOpen(true);
  };

  useEffect(() => {
    dispatch(getPlaneList({
      query: searchQuery,
      page: currentPage
    }))
    dispatch(getTariff({}))

    return () => {
      dispatch(clearPlaneList());
      dispatch(clearTariff());
    }
  }, [])

  const loadMore = () => {
    dispatch(getPlaneList({
      query: searchQuery,
      page: currentPage + 1,
    }));

    setCurrentPage(prev => prev + 1);
  }

  const search = () => {
    dispatch(getPlaneList({
      query: searchQuery,
      page: InitialPage,
    }));

    setCurrentPage(InitialPage);
  }

  const addAircraftButton = (
    <CustomButton
      heightSize={isMobile ? 's' : 'm'}
      type={'primary'}
      onClick={handleAddAircraftClick}
    >
      + {intl.formatMessage(FleetPageMessages.addPlane)}
    </CustomButton>
  );

  return (
    <PageWrapper>
      <CustomCard title={intl.formatMessage(FleetPageMessages.title)} width={'max'} isContainerCard={true}
                  extra={addAircraftButton} titlePosition={'space-between'}>
        <Row gutter={8} align={"middle"}>
          <Col flex={1}>
            <CustomSearch placeholder={intl.formatMessage(FleetPageMessages.search)}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onSearch={search}/>
          </Col>
        </Row>
        {items.length === 0
          ? (isLoading ? <Loader/> : <CustomResult title={intl.formatMessage(FleetPageMessages.noPlanes)}/>)
          : <PlaneList/>
        }

        {items.length > 0 && totalCount > items.length &&
          <CustomButton
            loading={isLoading}
            type={'primary'}
            onClick={loadMore}
          >
            {intl.formatMessage(CommonFormMessages.showMore)}
          </CustomButton>}
      </CustomCard>

      {isCreateOpen && <PlaneCreateDrawer isOpen={isCreateOpen} setIsOpen={setIsCreateOpen}/>}
    </PageWrapper>
  );
};
